import { EditorView } from "@codemirror/view"
import { autocompletion } from "@codemirror/autocomplete"
import type { Completion, CompletionContext } from "@codemirror/autocomplete"
import { AppNode } from "@features/builder/graph/types"
import { NodesMapper } from "@features/builder/helpers/nodesMapper"
import { getParentsList } from "@utils/node/getParentsList"
import { EXPRESSION_RESERVED_KEYWORDS } from "@utils/node/keywords"

export function expressionCompletion(node: AppNode, mapper: NodesMapper) {
  const parents = getParentsList(node)

  const labelCompletions: Completion[] = []
  for (const parentId of parents) {
    const label = mapper.idToLabelMap.get(parentId) ?? ""
    labelCompletions.push({
      label,
      type: "variable" as const,
      info: `Node: ${label}`,
      apply: label,
    })
  }

  const functionCompletions = EXPRESSION_RESERVED_KEYWORDS.map((func) => ({
    label: func,
    type: "function" as const,
    info: `Math function: ${func}()`,
    apply: (view: EditorView, _completion: Completion, from: number, to: number) => {
      view.dispatch({
        changes: { from, to, insert: `${func}()` },
        selection: { anchor: from + func.length + 1 },
      })
    },
  }))

  const getRowCompletions = (nodeLabel: string): Completion[] => {
    const node = mapper.nodesMap.get(mapper.labelToIdMap.get(nodeLabel) ?? "")
    if (node && "originalAttribute" in node.data) {
      const properties = node.data.originalAttribute.properties
      const rows: string[] = "index" in properties ? properties.index : []
      return rows.map((row) => ({
        label: row,
        type: "property" as const,
        info: `Row: ${row}`,
        apply: row,
      }))
    }
    return []
  }

  const getColCompletions = (nodeLabel: string): Completion[] => {
    const node = mapper.nodesMap.get(mapper.labelToIdMap.get(nodeLabel) ?? "")
    if (node && "originalAttribute" in node.data) {
      const properties = node.data.originalAttribute.properties
      const cols: string[] = "columns" in properties ? properties.columns : []
      return cols.map((col) => ({
        label: col,
        type: "property" as const,
        info: `Column: ${col}`,
        apply: col,
      }))
    }
    return []
  }

  const autoComplete = autocompletion({
    override: [
      (context: CompletionContext) => {
        const text = context.state.doc.toString()
        const before = text.slice(0, context.pos)
        const after = text.slice(context.pos)

        // Check if we're between braces
        const lastOpenBrace = before.lastIndexOf("{")
        const lastCloseBrace = before.lastIndexOf("}")
        const inBraces = lastOpenBrace > lastCloseBrace && after.indexOf("}") > -1

        if (inBraces) {
          // Get the text after the last { up to cursor
          // Node labels can contain any char except { } and cannot start with digit or contain -
          const nodeLabelStart = lastOpenBrace + 1
          const partialLabel = before.slice(nodeLabelStart)

          return {
            from: nodeLabelStart,
            options: labelCompletions,
          }
        }

        // For non-brace contexts, use word boundary
        const word = context.matchBefore(/\w*/)
        if (!word || (word.from == word.to && !context.explicit)) return null

        const locCompletion = {
          label: "loc",
          type: "property" as const,
          info: "Location accessor",
          apply: "loc",
        }

        // Check if we're after .loc['
        const locRowMatch = before.match(/\{([^}]+)\}\.loc\['([^']*)$/)
        if (locRowMatch) {
          const nodeLabel = locRowMatch[1]
          return {
            from: word.from,
            options: getRowCompletions(nodeLabel ?? ""),
          }
        }

        // Check if we're after .loc['row']['
        const locColMatch = before.match(/\{([^}]+)\}\.loc\['([^']+)'\]\['([^']*)$/)
        if (locColMatch) {
          const nodeLabel = locColMatch[1]
          return {
            from: word.from,
            options: getColCompletions(nodeLabel ?? ""),
          }
        }

        return {
          from: word.from,
          options: [...functionCompletions, locCompletion],
        }
      },
    ],
  })
  return autoComplete
}
